import React, { useState, ReactNode } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Card, CardContent } from "../components/ui/card";
import ChatWidget from "../components/ChatWidget";
import SeasonalDecorations from "../components/SeasonalDecorations";
import LatestBlogPosts from "../components/LatestBlogPosts";
import { useToast } from "../hooks/use-toast";
import { useSEO, SEO_PRESETS } from "../hooks/useSEO";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { CheckCircle, Shield, Clock, MapPin, Phone, Mail, Laptop2, Users, ArrowRight, Star, Zap, Gift, Heart } from "lucide-react";
import ChatbotProductSection from "../components/landing/ChatbotProductSection";
import KlartextCheckTeaser from "../components/landing/KlartextCheckTeaser";
import { postLead } from "../lib/api";

// New modern components
import ModernNav from "../components/landing/ModernNav";
import ModernHero from "../components/landing/ModernHero";
import TrustLogos from "../components/landing/TrustLogos";
import SocialSidebar from "../components/landing/SocialSidebar";
import SectionHeader from "../components/landing/SectionHeader";
import AISection from "../components/landing/AISection";
import Reveal from "../components/landing/Reveal";

// Dynamic section components
import { SectionsProvider, DynamicSection } from "../components/landing/SectionsProvider";
import { DynamicWhy } from "../components/landing/DynamicWhy";
import { DynamicChristmas } from "../components/landing/DynamicChristmas";
import { DynamicFAQ } from "../components/landing/DynamicFAQ";
import { DynamicTimes } from "../components/landing/DynamicTimes";
import { DynamicGiftVoucher } from "../components/landing/DynamicGiftVoucher";
import { DynamicServices } from "../components/landing/DynamicServices";
import { DynamicPrices } from "../components/landing/DynamicPrices";

interface SectionProps {
  id?: string;
  className?: string;
  children: ReactNode;
  alternate?: boolean;
}

export const Section = ({ id, className = "", children, alternate = false }: SectionProps) => (
  <section id={id} className={`relative py-20 md:py-28 overflow-hidden ${alternate ? 'bg-muted/40' : 'bg-background'} ${className}`}>
    <div className="mx-auto w-full max-w-7xl px-6">{children}</div>
  </section>
);

const Why = () => {
  const benefits = [
    { title: "Lokaler Service", desc: "Persönlicher Kontakt in Waren (Müritz) und Umgebung" },
    { title: "Verständlich erklärt", desc: "Keine Fachsprache - wir sprechen Ihre Sprache" },
    { title: "Geduldige Hilfe", desc: "Wir nehmen uns Zeit für Ihre Fragen" },
    { title: "Faire Preise", desc: "Transparente Kosten ohne versteckte Gebühren" },
    { title: "Flexibel verfügbar", desc: "Remote-Support oder Hausbesuch nach Wunsch" },
    { title: "Zuverlässig", desc: "Termine werden eingehalten, Versprechen gehalten" }
  ];

  const icons = [CheckCircle, Clock, Shield, MapPin, Users, Laptop2];

  return (
    <Section id="warum" alternate>
      <div className="absolute inset-0 bg-dotgrid opacity-40 pointer-events-none" aria-hidden="true" />
      <div className="relative">
        <SectionHeader
          number="01"
          title="Warum"
          highlight="Klartech Müritz?"
          subtitle="Ihr vertrauensvoller IT-Partner in Waren (Müritz) – geduldige, zuverlässige und verständliche Computer-Hilfe für alle Technik-Fragen."
        />

        <div className="grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
          {benefits.map((b, i) => {
            const Icon = icons[i % icons.length];
            return (
              <Reveal
                key={b.title}
                delay={(i % 3) * 90}
                className="group relative rounded-3xl border border-navy/10 bg-card p-6 md:p-8 shadow-soft transition-all duration-300 hover:shadow-lift hover:border-primary/35 hover:-translate-y-1.5"
              >
                {/* Leiterbahn-Punkt */}
                <span className="absolute top-6 right-6 h-2.5 w-2.5 rounded-full border-2 border-flame/45 transition-colors group-hover:border-flame" aria-hidden="true" />

                <div className="absolute -top-6 left-8 z-10">
                  <div className="inline-flex rounded-2xl bg-navy p-4 shadow-soft transition-colors duration-300 group-hover:bg-primary">
                    <Icon size={24} className="text-primary-foreground" />
                  </div>
                </div>
                <div className="pt-6 relative z-20">
                  <h3 className="text-lg font-bold text-navy mb-3">{b.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{b.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </Section>
  );
};

const Services = () => {
  return (
    <Section id="leistungen">
      <SectionHeader
        number="02"
        title="Unsere"
        highlight="Leistungen"
        subtitle="Maßgeschneiderte IT-Unterstützung für Ihren Bedarf"
      />

      <div className="grid items-stretch gap-8 lg:grid-cols-2">
        <Reveal className="group h-full">
          <div className="h-full flex flex-col rounded-[2rem] border border-navy/10 bg-card p-6 md:p-8 shadow-soft transition-all duration-500 hover:shadow-lift hover:border-primary/30">
            <div className="mb-6">
              <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 border border-primary/20 px-4 py-2 text-sm font-bold text-primary mb-4">
                <Laptop2 size={16} />
                Für Kleinunternehmen & Freiberufler
              </div>
              <h3 className="text-2xl font-bold text-navy mb-3">Business IT-Support</h3>
              <p className="text-muted-foreground leading-relaxed mb-6">
                Schneller Remote-Support bei PC-Problemen und professionelle IT-Beratung.
              </p>
            </div>

            <ul className="space-y-4 mb-8">
              <li className="flex items-start gap-3 text-foreground/85">
                <CheckCircle size={20} className="mt-0.5 shrink-0 text-primary" />
                <span>Schneller Remote-Support bei PC-Problemen</span>
              </li>
              <li className="flex items-start gap-3 text-foreground/85">
                <CheckCircle size={20} className="mt-0.5 shrink-0 text-primary" />
                <span>Unterstützung bei Bürosoftware (z.B. M365)</span>
              </li>
              <li className="flex items-start gap-3 text-foreground/85">
                <CheckCircle size={20} className="mt-0.5 shrink-0 text-primary" />
                <span>Beratung zu Datensicherung & IT-Sicherheit</span>
              </li>
              <li className="flex items-start gap-3 text-foreground/85">
                <CheckCircle size={20} className="mt-0.5 shrink-0 text-primary" />
                <span>Grundlegende Website-Pflege (Updates etc.)</span>
              </li>
              <li className="flex items-start gap-3 text-foreground/85">
                <CheckCircle size={20} className="mt-0.5 shrink-0 text-primary" />
                <span>Technik-orientierte virtuelle Assistenz</span>
              </li>
            </ul>

            <div className="relative overflow-hidden rounded-2xl shadow-soft group-hover:shadow-lift transition-shadow mt-auto">
              <img
                src="https://images.pexels.com/photos/1181472/pexels-photo-1181472.jpeg"
                alt="Business IT Support"
                loading="lazy"
                className="h-64 w-full object-cover transform group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-deep/40 via-transparent to-transparent"></div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={120} className="group h-full">
          <div className="h-full flex flex-col rounded-[2rem] border border-navy/10 bg-card p-6 md:p-8 shadow-soft transition-all duration-500 hover:shadow-lift hover:border-emerald-500/40">
            <div className="mb-6">
              <div className="inline-flex items-center gap-2 rounded-full bg-emerald-500/10 border border-emerald-500/25 px-4 py-2 text-sm font-bold text-emerald-700 mb-4">
                <Users size={16} />
                Für Senioren & Privatpersonen
              </div>
              <h3 className="text-2xl font-bold text-navy mb-3">Senior IT-Hilfe</h3>
              <p className="text-muted-foreground leading-relaxed mb-6">
                Geduldige, verständliche Unterstützung – Schritt für Schritt.
              </p>
            </div>

            <ul className="space-y-4 mb-8">
              <li className="flex items-start gap-3 text-foreground/85">
                <CheckCircle size={20} className="mt-0.5 shrink-0 text-emerald-600" />
                <span>Bedienung von Smartphone, Tablet, PC</span>
              </li>
              <li className="flex items-start gap-3 text-foreground/85">
                <CheckCircle size={20} className="mt-0.5 shrink-0 text-emerald-600" />
                <span>Sicheres Internet, E-Mail & Online-Banking</span>
              </li>
              <li className="flex items-start gap-3 text-foreground/85">
                <CheckCircle size={20} className="mt-0.5 shrink-0 text-emerald-600" />
                <span>Einrichtung von Videoanrufen</span>
              </li>
              <li className="flex items-start gap-3 text-foreground/85">
                <CheckCircle size={20} className="mt-0.5 shrink-0 text-emerald-600" />
                <span>Schutz vor Viren & Online-Betrug</span>
              </li>
              <li className="flex items-start gap-3 text-foreground/85">
                <CheckCircle size={20} className="mt-0.5 shrink-0 text-emerald-600" />
                <span>Hilfe bei Drucker, WLAN & Co.</span>
              </li>
              <li className="flex items-start gap-3 text-foreground/85">
                <CheckCircle size={20} className="mt-0.5 shrink-0 text-emerald-600" />
                <span>Geduldige Schulungen & Erklärungen</span>
              </li>
            </ul>

            <div className="relative overflow-hidden rounded-2xl shadow-soft group-hover:shadow-lift transition-shadow mt-auto">
              <img
                src="https://images.pexels.com/photos/3768140/pexels-photo-3768140.jpeg"
                alt="Senior IT Hilfe"
                loading="lazy"
                className="h-64 w-full object-cover transform group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-deep/40 via-transparent to-transparent"></div>
            </div>
          </div>
        </Reveal>
      </div>
    </Section>
  );
};

// Weihnachts-Spezial: Neue Geräte einrichten
const ChristmasDeviceSetup = () => {
  return (
    <Section className="bg-gradient-to-br from-red-50/60 via-background to-emerald-50/60 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-40 h-40 bg-red-100/70 rounded-full blur-3xl opacity-60" aria-hidden="true"></div>
      <div className="absolute bottom-0 right-0 w-40 h-40 bg-emerald-100/70 rounded-full blur-3xl opacity-60" aria-hidden="true"></div>

      <div className="relative z-10">
        <Reveal className="text-center mb-12">
          <div className="inline-flex items-center gap-2 rounded-full bg-red-100 border border-red-200 px-5 py-2 text-red-700 font-bold mb-6">
            <span className="text-xl">🎄</span>
            Weihnachts-Spezial
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-navy mb-4">
            Neues Gerät zu <span className="text-red-600">Weihnachten</span>?
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Tablet, Smartphone oder Laptop geschenkt bekommen? Ich helfe bei der Einrichtung!
          </p>
        </Reveal>

        <div className="grid gap-6 lg:grid-cols-3">
          <Reveal>
            <Card className="h-full p-6 bg-card/90 backdrop-blur rounded-3xl border border-red-100 hover:border-red-300 transition-all hover:shadow-lift hover:-translate-y-1 shadow-soft">
              <div className="text-4xl mb-4">📱</div>
              <h3 className="text-xl font-bold text-navy mb-3">Smartphone & Tablet</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="mt-1 text-emerald-600 shrink-0" />
                  <span>Grundeinrichtung & WLAN</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="mt-1 text-emerald-600 shrink-0" />
                  <span>WhatsApp & Video-Telefonie</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="mt-1 text-emerald-600 shrink-0" />
                  <span>Kontakte & Fotos übertragen</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="mt-1 text-emerald-600 shrink-0" />
                  <span>Wichtige Apps installieren</span>
                </li>
              </ul>
            </Card>
          </Reveal>

          <Reveal delay={90}>
            <Card className="h-full p-6 bg-card/90 backdrop-blur rounded-3xl border border-emerald-100 hover:border-emerald-300 transition-all hover:shadow-lift hover:-translate-y-1 shadow-soft">
              <div className="text-4xl mb-4">💻</div>
              <h3 className="text-xl font-bold text-navy mb-3">Laptop & PC</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="mt-1 text-emerald-600 shrink-0" />
                  <span>Windows einrichten</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="mt-1 text-emerald-600 shrink-0" />
                  <span>E-Mail & Browser konfigurieren</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="mt-1 text-emerald-600 shrink-0" />
                  <span>Drucker & Scanner verbinden</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="mt-1 text-emerald-600 shrink-0" />
                  <span>Datensicherung einrichten</span>
                </li>
              </ul>
            </Card>
          </Reveal>

          <Reveal delay={180}>
            <Card className="h-full p-6 bg-card/90 backdrop-blur rounded-3xl border border-primary/15 hover:border-primary/40 transition-all hover:shadow-lift hover:-translate-y-1 shadow-soft">
              <div className="text-4xl mb-4">🏠</div>
              <h3 className="text-xl font-bold text-navy mb-3">Smart Home</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="mt-1 text-emerald-600 shrink-0" />
                  <span>Smart-TV einrichten</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="mt-1 text-emerald-600 shrink-0" />
                  <span>Streaming-Dienste (Netflix & Co.)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="mt-1 text-emerald-600 shrink-0" />
                  <span>Alexa & Google Home</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="mt-1 text-emerald-600 shrink-0" />
                  <span>WLAN-Optimierung</span>
                </li>
              </ul>
            </Card>
          </Reveal>
        </div>

        <Reveal className="mt-12 text-center">
          <div className="inline-flex flex-col sm:flex-row gap-4">
            <Button
              onClick={() => document.querySelector("#kontakt")?.scrollIntoView({ behavior: "smooth" })}
              className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg font-bold rounded-full shadow-lift transition-all hover:-translate-y-0.5"
            >
              🎁 Termin vereinbaren
            </Button>
            <a href="/gutschein">
              <Button
                variant="outline"
                className="border-2 border-red-300 text-red-700 hover:bg-red-50 px-8 py-6 text-lg font-bold rounded-full"
              >
                🎄 Als Gutschein verschenken
              </Button>
            </a>
          </div>
          <p className="mt-4 text-muted-foreground">
            Ideal als Geschenk für Eltern oder Großeltern!
          </p>
        </Reveal>
      </div>
    </Section>
  );
};

const Times = () => {
  return (
    <Section id="zeiten" alternate>
      <SectionHeader
        number="04"
        title="Arbeitszeiten &"
        highlight="Verfügbarkeit"
        centered={false}
      />

      <div className="grid items-center gap-16 lg:grid-cols-2">
        <div>
          <Reveal>
            <p className="text-xl text-muted-foreground leading-relaxed mb-8">
              Ich arbeite überwiegend remote und bin in der Regel Montag bis Freitag von 8:00–17:00 Uhr erreichbar.
              Vor-Ort-Termine nach Absprache, primär in den Abendstunden.
            </p>
          </Reveal>

          <div className="space-y-4">
            <Reveal delay={60}>
              <div className="flex items-start gap-4 p-5 rounded-2xl bg-card border border-navy/10 shadow-soft transition-all hover:shadow-lift hover:border-primary/30">
                <div className="p-2.5 rounded-full bg-primary/10">
                  <Clock size={18} className="text-primary" />
                </div>
                <span className="text-foreground/85 leading-relaxed font-medium">
                  <strong className="text-navy">Montag, Dienstag, Donnerstag, Freitag:</strong> Vor-Ort-Termine abends möglich
                </span>
              </div>
            </Reveal>

            <Reveal delay={120}>
              <div className="flex items-start gap-4 p-5 rounded-2xl bg-card border border-navy/10 shadow-soft transition-all hover:shadow-lift hover:border-primary/30">
                <div className="p-2.5 rounded-full bg-red-100">
                  <Clock size={18} className="text-red-600" />
                </div>
                <span className="text-foreground/85 leading-relaxed font-medium">
                  <strong className="text-navy">Mittwoch:</strong> Keine Vor-Ort-Termine möglich
                </span>
              </div>
            </Reveal>

            <Reveal delay={180}>
              <div className="flex items-start gap-4 p-5 rounded-2xl bg-card border border-navy/10 shadow-soft transition-all hover:shadow-lift hover:border-primary/30">
                <div className="p-2.5 rounded-full bg-flame/10">
                  <Clock size={18} className="text-flame" />
                </div>
                <span className="text-foreground/85 leading-relaxed font-medium">
                  <strong className="text-navy">Samstag:</strong> In Ausnahmefällen nach vorheriger Vereinbarung auch samstags Vor-Ort-Termine möglich
                </span>
              </div>
            </Reveal>

            <Reveal delay={240}>
              <div className="flex items-start gap-4 p-5 rounded-2xl bg-card border border-navy/10 shadow-soft transition-all hover:shadow-lift hover:border-primary/30">
                <div className="p-2.5 rounded-full bg-muted">
                  <Clock size={18} className="text-muted-foreground" />
                </div>
                <span className="text-foreground/85 leading-relaxed font-medium">
                  <strong className="text-navy">Sonntag & Feiertage:</strong> Generell keine Vor-Ort-Termine
                </span>
              </div>
            </Reveal>
          </div>
        </div>

        <Reveal delay={150} className="relative">
          <div className="absolute -top-8 -right-8 w-64 h-64 bg-gradient-to-br from-accent/15 to-flame/10 rounded-full blur-3xl" aria-hidden="true"></div>

          {/* Leiterbahn-Rahmen */}
          <div className="absolute -bottom-5 -left-5 w-full h-full rounded-[2rem] border-2 border-primary/20 pointer-events-none" aria-hidden="true">
            <span className="absolute -bottom-[7px] right-12 h-3 w-3 rounded-full border-2 border-primary/50 bg-background" />
          </div>

          <Card className="overflow-hidden border border-navy/10 shadow-lift rounded-[2rem] relative z-10">
            <CardContent className="p-0 relative">
              <div className="absolute top-6 right-6 z-10">
                <div className="bg-background/90 backdrop-blur-sm rounded-full px-4 py-2 shadow-soft border border-border">
                  <div className="flex items-center gap-2 text-sm font-bold text-navy">
                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                    Mo-Fr 8:00-17:00
                  </div>
                </div>
              </div>
              <img
                src="https://images.unsplash.com/photo-1573164713714-d95e436ab8d6"
                alt="Remote Support Arbeitsplatz"
                loading="lazy"
                className="h-96 w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-deep/40 via-transparent to-transparent"></div>
            </CardContent>
          </Card>
        </Reveal>
      </div>
    </Section>
  );
};

const Prices = () => {
  return (
    <Section id="preise">
      <SectionHeader
        number="05"
        title="Faire"
        highlight="Preise"
        subtitle="Einfach, verständlich und ohne Fachchinesisch. Alle Preise sind Endpreise gemäß § 19 UStG (Kleinunternehmerregelung)."
      />

      <div className="grid gap-10 lg:grid-cols-2 items-start">
        {/* Business Preise */}
        <Reveal className="bg-card rounded-[2rem] p-5 md:p-8 shadow-soft border border-navy/10 hover:shadow-lift transition-all duration-500">
          <div className="flex items-center gap-4 mb-6">
            <div className="p-3.5 rounded-2xl bg-navy shadow-soft">
              <Laptop2 size={24} className="text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-navy">Kleinunternehmen & Freiberufler</h3>
              <p className="text-muted-foreground">IT-Betreuung im Monatsabo</p>
            </div>
          </div>

          {/* Retainer-Pakete - MSP Focus */}
          <div className="mb-10">
            <div className="text-lg font-bold text-navy mb-4 flex items-center gap-2">
              <Shield size={18} className="text-emerald-600" />
              Retainer-Pakete (Monatsabo)
            </div>

            <p className="text-muted-foreground mb-6 text-sm leading-relaxed bg-primary/5 p-4 rounded-2xl border border-primary/15">
              Statt einzelne Einsätze zu bezahlen, sichern Sie sich mit unseren Retainer-Paketen eine laufende IT-Betreuung – planbar, kalkulierbar und mit klar definierten Reaktionszeiten.
            </p>

            <div className="space-y-4">
              {/* Retainer Basis */}
              <div className="group relative rounded-2xl border border-navy/[0.12] bg-background p-6 transition-all hover:border-primary/50 hover:shadow-lift">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="font-bold text-navy text-lg mb-1">Retainer Basis</div>
                    <p className="text-sm text-muted-foreground mb-3 italic">
                      Für Solo-Selbstständige und kleine Büros, die ihre IT nicht sich selbst überlassen wollen.
                    </p>
                    <ul className="space-y-1.5">
                      <li className="flex items-start gap-2 text-muted-foreground text-sm">
                        <CheckCircle size={16} className="mt-0.5 text-emerald-500 shrink-0" />
                        <span>1× monatlicher IT-Gesundheitscheck (Updates, Virenschutz, Backup-Kontrolle)</span>
                      </li>
                      <li className="flex items-start gap-2 text-muted-foreground text-sm">
                        <CheckCircle size={16} className="mt-0.5 text-emerald-500 shrink-0" />
                        <span>30 Minuten Remote-Support inklusive</span>
                      </li>
                      <li className="flex items-start gap-2 text-muted-foreground text-sm">
                        <CheckCircle size={16} className="mt-0.5 text-emerald-500 shrink-0" />
                        <span>Reaktionszeit: innerhalb von 48 Stunden an Werktagen</span>
                      </li>
                      <li className="flex items-start gap-2 text-muted-foreground text-sm">
                        <CheckCircle size={16} className="mt-0.5 text-emerald-500 shrink-0" />
                        <span>10% Rabatt auf zusätzliche Leistungen</span>
                      </li>
                    </ul>
                  </div>
                  <div className="md:ml-6 flex items-center justify-between gap-3 md:flex-col md:items-end md:justify-start">
                    <div className="text-left md:text-right">
                      <div className="text-2xl font-extrabold text-navy leading-tight whitespace-nowrap">ab 99 €</div>
                      <div className="text-sm font-semibold text-muted-foreground">/ Monat</div>
                    </div>
                    <Button
                      onClick={() => document.querySelector("#kontakt")?.scrollIntoView({ behavior: "smooth" })}
                      variant="outline"
                      size="sm"
                      className="text-sm rounded-full border-primary text-primary hover:bg-primary hover:text-white"
                    >
                      Jetzt anfragen
                    </Button>
                  </div>
                </div>
              </div>

              {/* Retainer Standard - Empfohlen */}
              <div className="group relative rounded-2xl border-2 border-primary bg-primary/[0.04] p-6 transition-all hover:shadow-lift shadow-soft">
                <div className="absolute -top-3 left-6">
                  <span className="bg-gradient-to-r from-primary to-accent text-white text-xs font-bold px-3 py-1 rounded-full shadow-md">
                    ⭐ Empfohlen
                  </span>
                </div>
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mt-2">
                  <div className="flex-1">
                    <div className="font-bold text-navy text-lg mb-1">Retainer Standard</div>
                    <p className="text-sm text-muted-foreground mb-3 italic">
                      Für kleine Teams, die sich auf eine dauerhaft funktionierende IT verlassen müssen.
                    </p>
                    <ul className="space-y-1.5">
                      <li className="flex items-start gap-2 text-muted-foreground text-sm">
                        <CheckCircle size={16} className="mt-0.5 text-emerald-500 shrink-0" />
                        <span>1× monatlicher IT-Gesundheitscheck</span>
                      </li>
                      <li className="flex items-start gap-2 text-muted-foreground text-sm">
                        <CheckCircle size={16} className="mt-0.5 text-emerald-500 shrink-0" />
                        <span>60 Minuten Remote-Support inklusive</span>
                      </li>
                      <li className="flex items-start gap-2 text-muted-foreground text-sm">
                        <CheckCircle size={16} className="mt-0.5 text-emerald-500 shrink-0" />
                        <span>Reaktionszeit: innerhalb von 24 Stunden an Werktagen</span>
                      </li>
                      <li className="flex items-start gap-2 text-muted-foreground text-sm">
                        <CheckCircle size={16} className="mt-0.5 text-emerald-500 shrink-0" />
                        <span>15% Rabatt auf zusätzliche Leistungen</span>
                      </li>
                    </ul>
                  </div>
                  <div className="md:ml-6 flex items-center justify-between gap-3 md:flex-col md:items-end md:justify-start">
                    <div className="text-left md:text-right">
                      <div className="text-2xl font-extrabold text-navy leading-tight whitespace-nowrap">ab 149 €</div>
                      <div className="text-sm font-semibold text-muted-foreground">/ Monat</div>
                    </div>
                    <Button
                      onClick={() => document.querySelector("#kontakt")?.scrollIntoView({ behavior: "smooth" })}
                      size="sm"
                      className="text-sm rounded-full bg-primary hover:bg-primary/90 text-white"
                    >
                      Jetzt anfragen
                    </Button>
                  </div>
                </div>
              </div>

              {/* Retainer Premium */}
              <div className="group relative rounded-2xl border border-navy/[0.12] bg-background p-6 transition-all hover:border-primary/50 hover:shadow-lift">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="font-bold text-navy text-lg mb-1">Retainer Premium</div>
                    <p className="text-sm text-muted-foreground mb-3 italic">
                      Für Betriebe, bei denen IT-Ausfälle richtig teuer werden.
                    </p>
                    <ul className="space-y-1.5">
                      <li className="flex items-start gap-2 text-muted-foreground text-sm">
                        <CheckCircle size={16} className="mt-0.5 text-emerald-500 shrink-0" />
                        <span>1× monatlicher IT-Gesundheitscheck</span>
                      </li>
                      <li className="flex items-start gap-2 text-muted-foreground text-sm">
                        <CheckCircle size={16} className="mt-0.5 text-emerald-500 shrink-0" />
                        <span>120 Minuten Remote-Support inklusive</span>
                      </li>
                      <li className="flex items-start gap-2 text-muted-foreground text-sm">
                        <CheckCircle size={16} className="mt-0.5 text-emerald-500 shrink-0" />
                        <span>1 Vor-Ort-Einsatz pro Quartal inklusive (bis 2 Stunden)</span>
                      </li>
                      <li className="flex items-start gap-2 text-muted-foreground text-sm">
                        <CheckCircle size={16} className="mt-0.5 text-emerald-500 shrink-0" />
                        <span>Priorisierte Bearbeitung (Reaktion möglichst am selben Werktag)</span>
                      </li>
                      <li className="flex items-start gap-2 text-muted-foreground text-sm">
                        <CheckCircle size={16} className="mt-0.5 text-emerald-500 shrink-0" />
                        <span>20% Rabatt auf zusätzliche Leistungen</span>
                      </li>
                    </ul>
                  </div>
                  <div className="md:ml-6 flex items-center justify-between gap-3 md:flex-col md:items-end md:justify-start">
                    <div className="text-left md:text-right">
                      <div className="text-2xl font-extrabold text-navy leading-tight whitespace-nowrap">ab 199 €</div>
                      <div className="text-sm font-semibold text-muted-foreground">/ Monat</div>
                    </div>
                    <Button
                      onClick={() => document.querySelector("#kontakt")?.scrollIntoView({ behavior: "smooth" })}
                      variant="outline"
                      size="sm"
                      className="text-sm rounded-full border-primary text-primary hover:bg-primary hover:text-white"
                    >
                      Jetzt anfragen
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Einmal-Support ohne Vertrag */}
          <div className="mb-10">
            <div className="text-lg font-bold text-navy mb-4 flex items-center gap-2">
              <Zap size={18} className="text-flame" />
              Einmal-Support ohne Vertrag
            </div>

            {/* Desktop: Standard Table */}
            <div className="hidden md:block">
              <Table className="rounded-2xl overflow-hidden border border-navy/10">
                <TableHeader className="bg-muted/70">
                  <TableRow>
                    <TableHead className="font-bold text-navy">Leistung</TableHead>
                    <TableHead className="font-bold text-navy">Beschreibung</TableHead>
                    <TableHead className="text-right font-bold text-navy">Preis</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-medium">Remote-Support (Stundenbasis)</TableCell>
                    <TableCell className="text-muted-foreground">Schnelle Hilfe per Telefon, Video oder Fernwartung. Abrechnung je angefangene 15 Minuten, Mindestberechnung 30 Minuten.</TableCell>
                    <TableCell className="text-right font-bold text-primary whitespace-nowrap">69,00 € / Std.</TableCell>
                  </TableRow>
                  <TableRow className="bg-muted/40">
                    <TableCell className="font-medium">Vor-Ort-Support (Stundenbasis)</TableCell>
                    <TableCell className="text-muted-foreground">Persönlicher IT-Support in Waren (Müritz) & Umgebung. Anfahrt inkl. Abrechnung je 15 Minuten, Mindestberechnung 60 Minuten.</TableCell>
                    <TableCell className="text-right font-bold text-primary whitespace-nowrap">90,00 € / Std.</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">IT-Check für Kleinunternehmen</TableCell>
                    <TableCell className="text-muted-foreground">Bestandsaufnahme mit Schwachstellenanalyse & schriftlichem Bericht.</TableCell>
                    <TableCell className="text-right font-bold text-primary whitespace-nowrap">103,50 € (einmalig)</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>

            {/* Mobile: Card Layout */}
            <div className="md:hidden space-y-4">
              <div className="rounded-2xl border border-navy/10 bg-background p-4 shadow-soft">
                <div className="flex justify-between items-start mb-3 gap-2">
                  <h4 className="font-bold text-navy text-base">Remote-Support</h4>
                  <div className="bg-primary/10 text-primary px-3 py-1 rounded-full font-bold text-sm whitespace-nowrap">
                    69,00 € / Std.
                  </div>
                </div>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Schnelle Hilfe per Telefon, Video oder Fernwartung. Abrechnung je angefangene 15 Minuten, Mindestberechnung 30 Minuten.
                </p>
              </div>

              <div className="rounded-2xl border border-navy/10 bg-muted/40 p-4 shadow-soft">
                <div className="flex justify-between items-start mb-3 gap-2">
                  <h4 className="font-bold text-navy text-base">Vor-Ort-Support</h4>
                  <div className="bg-primary/10 text-primary px-3 py-1 rounded-full font-bold text-sm whitespace-nowrap">
                    90,00 € / Std.
                  </div>
                </div>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Persönlicher IT-Support in Waren (Müritz) & Umgebung. Anfahrt inkl. Abrechnung je 15 Minuten, Mindestberechnung 60 Minuten.
                </p>
              </div>

              <div className="rounded-2xl border border-navy/10 bg-background p-4 shadow-soft">
                <div className="flex justify-between items-start mb-3 gap-2">
                  <h4 className="font-bold text-navy text-base">IT-Check für Kleinunternehmen</h4>
                  <div className="bg-primary/10 text-primary px-3 py-1 rounded-full font-bold text-sm whitespace-nowrap">
                    103,50 €
                  </div>
                </div>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Bestandsaufnahme mit Schwachstellenanalyse & schriftlichem Bericht.
                </p>
              </div>
            </div>
          </div>

          {/* Website-Services */}
          <div>
            <div className="text-lg font-bold text-navy mb-4 flex items-center gap-2">
              <Star size={18} className="text-flame" />
              Website-Services
            </div>
            {/* Desktop: Standard Table */}
            <div className="hidden md:block">
              <Table className="rounded-2xl overflow-hidden border border-navy/10">
                <TableHeader className="bg-muted/70">
                  <TableRow>
                    <TableHead className="font-bold text-navy">Leistung</TableHead>
                    <TableHead className="font-bold text-navy">Beschreibung</TableHead>
                    <TableHead className="text-right font-bold text-navy">Preis</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-medium">Website-Wartung WordPress</TableCell>
                    <TableCell className="text-muted-foreground">Monatliche Updates, Backups, Sicherheits-Scans.</TableCell>
                    <TableCell className="text-right font-bold text-primary whitespace-nowrap">ab 48,00 € / Monat</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>

            {/* Mobile: Card Layout */}
            <div className="md:hidden space-y-3">
              <div className="rounded-2xl border border-navy/10 bg-background p-4 shadow-soft">
                <div className="flex justify-between items-start mb-3 gap-2">
                  <h4 className="font-bold text-navy text-base">Website-Wartung WordPress</h4>
                  <div className="bg-primary/10 text-primary px-3 py-1 rounded-full font-bold text-sm whitespace-nowrap">
                    ab 48 € / Mon.
                  </div>
                </div>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Monatliche Updates, Backups, Sicherheits-Scans.
                </p>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Senioren Preise */}
        <Reveal delay={120} className="bg-card rounded-[2rem] p-5 md:p-8 shadow-soft border border-navy/10 hover:shadow-lift transition-all duration-500">
          <div className="flex items-center gap-4 mb-6">
            <div className="p-3.5 rounded-2xl bg-emerald-600 shadow-soft">
              <Users size={24} className="text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-navy">Senioren & Privatpersonen</h3>
              <p className="text-muted-foreground">Geduldige, verständliche Hilfe</p>
            </div>
          </div>

          {/* Sorglos-Pakete für Zuhause - NEU */}
          <div className="mb-10">
            <div className="text-lg font-bold text-navy mb-4 flex items-center gap-2">
              <Heart size={18} className="text-flame" />
              Sorglos-Pakete für Zuhause (Monatsabo)
            </div>

            <p className="text-muted-foreground mb-6 text-sm leading-relaxed bg-emerald-500/5 p-4 rounded-2xl border border-emerald-500/20">
              Für alle, die regelmäßig Unterstützung brauchen, aber nicht jedes Mal neu überlegen wollen, wen sie anrufen.
            </p>

            <div className="space-y-4">
              {/* Sorglos Zuhause Basic */}
              <div className="group relative rounded-2xl border border-navy/[0.12] bg-background p-6 transition-all hover:border-emerald-500/60 hover:shadow-lift">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="font-bold text-navy text-lg mb-2">Sorglos Zuhause Basic</div>
                    <ul className="space-y-1.5">
                      <li className="flex items-start gap-2 text-muted-foreground text-sm">
                        <CheckCircle size={16} className="mt-0.5 text-emerald-500 shrink-0" />
                        <span>1× Remote-Sicherheitscheck alle 6 Monate (Updates, Virenschutz, grundlegende Prüfung)</span>
                      </li>
                      <li className="flex items-start gap-2 text-muted-foreground text-sm">
                        <CheckCircle size={16} className="mt-0.5 text-emerald-500 shrink-0" />
                        <span>15 Minuten Telefonsupport pro Monat inklusive (z.B. für kurze Fragen)</span>
                      </li>
                      <li className="flex items-start gap-2 text-muted-foreground text-sm">
                        <CheckCircle size={16} className="mt-0.5 text-emerald-500 shrink-0" />
                        <span>10% Rabatt auf Vor-Ort-Termine</span>
                      </li>
                    </ul>
                  </div>
                  <div className="md:ml-6 flex items-center justify-between gap-3 md:flex-col md:items-end md:justify-start">
                    <div className="text-left md:text-right">
                      <div className="text-2xl font-extrabold text-navy leading-tight whitespace-nowrap">19 €</div>
                      <div className="text-sm font-semibold text-muted-foreground">/ Monat</div>
                    </div>
                    <Button
                      onClick={() => document.querySelector("#kontakt")?.scrollIntoView({ behavior: "smooth" })}
                      variant="outline"
                      size="sm"
                      className="text-sm rounded-full border-emerald-500 text-emerald-600 hover:bg-emerald-500 hover:text-white"
                    >
                      Jetzt anfragen
                    </Button>
                  </div>
                </div>
              </div>

              {/* Sorglos Zuhause Plus */}
              <div className="group relative rounded-2xl border-2 border-emerald-500 bg-emerald-500/[0.04] p-6 transition-all hover:shadow-lift shadow-soft">
                <div className="absolute -top-3 left-6">
                  <span className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md">
                    💚 Beliebte Wahl
                  </span>
                </div>
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mt-2">
                  <div className="flex-1">
                    <div className="font-bold text-navy text-lg mb-2">Sorglos Zuhause Plus</div>
                    <ul className="space-y-1.5">
                      <li className="flex items-start gap-2 text-muted-foreground text-sm">
                        <CheckCircle size={16} className="mt-0.5 text-emerald-500 shrink-0" />
                        <span>1× Remote-Sicherheitscheck pro Quartal</span>
                      </li>
                      <li className="flex items-start gap-2 text-muted-foreground text-sm">
                        <CheckCircle size={16} className="mt-0.5 text-emerald-500 shrink-0" />
                        <span>30 Minuten Telefonsupport pro Monat inklusive</span>
                      </li>
                      <li className="flex items-start gap-2 text-muted-foreground text-sm">
                        <CheckCircle size={16} className="mt-0.5 text-emerald-500 shrink-0" />
                        <span>Bevorzugte Terminvergabe bei Vor-Ort-Einsätzen</span>
                      </li>
                      <li className="flex items-start gap-2 text-muted-foreground text-sm">
                        <CheckCircle size={16} className="mt-0.5 text-emerald-500 shrink-0" />
                        <span>15% Rabatt auf Vor-Ort-Termine</span>
                      </li>
                    </ul>
                  </div>
                  <div className="md:ml-6 flex items-center justify-between gap-3 md:flex-col md:items-end md:justify-start">
                    <div className="text-left md:text-right">
                      <div className="text-2xl font-extrabold text-navy leading-tight whitespace-nowrap">39 €</div>
                      <div className="text-sm font-semibold text-muted-foreground">/ Monat</div>
                    </div>
                    <Button
                      onClick={() => document.querySelector("#kontakt")?.scrollIntoView({ behavior: "smooth" })}
                      size="sm"
                      className="text-sm rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                      Jetzt anfragen
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Flexible Hilfe */}
          <div className="mb-10">
            <div className="text-lg font-bold text-navy mb-4 flex items-center gap-2">
              <Clock size={18} className="text-primary" />
              Flexible Hilfe (Einzeltermine)
            </div>
            {/* Desktop: Standard Table */}
            <div className="hidden md:block">
              <Table className="rounded-2xl overflow-hidden border border-navy/10">
                <TableHeader className="bg-muted/70">
                  <TableRow>
                    <TableHead className="font-bold text-navy">Leistung</TableHead>
                    <TableHead className="font-bold text-navy">Dauer</TableHead>
                    <TableHead className="text-right font-bold text-navy">Preis</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-medium">Remote-Support Kurz-Hilfe</TableCell>
                    <TableCell className="text-muted-foreground">30 Min.</TableCell>
                    <TableCell className="text-right font-bold text-primary">30,00 €</TableCell>
                  </TableRow>
                  <TableRow className="bg-muted/40">
                    <TableCell className="font-medium">Remote-Support Standard-Sitzung</TableCell>
                    <TableCell className="text-muted-foreground">60 Min.</TableCell>
                    <TableCell className="text-right font-bold text-primary">50,00 €</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Remote-Support Verlängerung</TableCell>
                    <TableCell className="text-muted-foreground">je 15 Min.</TableCell>
                    <TableCell className="text-right font-bold text-primary">12,50 €</TableCell>
                  </TableRow>
                  <TableRow className="bg-muted/40">
                    <TableCell className="font-medium">Vor-Ort-Support erste Stunde</TableCell>
                    <TableCell className="text-muted-foreground">60 Min.</TableCell>
                    <TableCell className="text-right font-bold text-primary">70,00 €</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Vor-Ort-Support Verlängerung</TableCell>
                    <TableCell className="text-muted-foreground">je 15 Min.</TableCell>
                    <TableCell className="text-right font-bold text-primary">15,00 €</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>

            {/* Mobile: Card Layout */}
            <div className="md:hidden space-y-3">
              <div className="rounded-2xl border border-navy/10 bg-background p-4 shadow-soft">
                <div className="flex justify-between items-center mb-2 gap-2">
                  <h4 className="font-bold text-navy text-sm">Remote-Support Kurz-Hilfe</h4>
                  <div className="bg-primary/10 text-primary px-3 py-1 rounded-full font-bold text-sm whitespace-nowrap">
                    30,00 €
                  </div>
                </div>
                <p className="text-muted-foreground text-xs">30 Minuten</p>
              </div>

              <div className="rounded-2xl border border-navy/10 bg-muted/40 p-4 shadow-soft">
                <div className="flex justify-between items-center mb-2 gap-2">
                  <h4 className="font-bold text-navy text-sm">Remote-Support Standard</h4>
                  <div className="bg-primary/10 text-primary px-3 py-1 rounded-full font-bold text-sm whitespace-nowrap">
                    50,00 €
                  </div>
                </div>
                <p className="text-muted-foreground text-xs">60 Minuten</p>
              </div>

              <div className="rounded-2xl border border-navy/10 bg-background p-4 shadow-soft">
                <div className="flex justify-between items-center mb-2 gap-2">
                  <h4 className="font-bold text-navy text-sm">Remote Verlängerung</h4>
                  <div className="bg-primary/10 text-primary px-3 py-1 rounded-full font-bold text-sm whitespace-nowrap">
                    12,50 €
                  </div>
                </div>
                <p className="text-muted-foreground text-xs">je 15 Minuten</p>
              </div>

              <div className="rounded-2xl border border-navy/10 bg-muted/40 p-4 shadow-soft">
                <div className="flex justify-between items-center mb-2 gap-2">
                  <h4 className="font-bold text-navy text-sm">Vor-Ort erste Stunde</h4>
                  <div className="bg-primary/10 text-primary px-3 py-1 rounded-full font-bold text-sm whitespace-nowrap">
                    70,00 €
                  </div>
                </div>
                <p className="text-muted-foreground text-xs">60 Minuten</p>
              </div>

              <div className="rounded-2xl border border-navy/10 bg-background p-4 shadow-soft">
                <div className="flex justify-between items-center mb-2 gap-2">
                  <h4 className="font-bold text-navy text-sm">Vor-Ort Verlängerung</h4>
                  <div className="bg-primary/10 text-primary px-3 py-1 rounded-full font-bold text-sm whitespace-nowrap">
                    15,00 €
                  </div>
                </div>
                <p className="text-muted-foreground text-xs">je 15 Minuten</p>
              </div>
            </div>
          </div>

          {/* Schulungen */}
          <div className="mb-10">
            <div className="text-lg font-bold text-navy mb-4 flex items-center gap-2">
              <Users size={18} className="text-emerald-600" />
              Schulungen
            </div>
            <div className="space-y-4">
              <div className="group relative rounded-2xl border border-navy/[0.12] bg-background p-6 transition-all hover:border-emerald-500/60 hover:shadow-lift">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="font-bold text-navy text-lg mb-2">Smartphone/Tablet-Schulung</div>
                    <div className="text-sm text-muted-foreground mb-2">60 Minuten</div>
                    <ul className="space-y-1.5">
                      <li className="flex items-start gap-2 text-muted-foreground">
                        <CheckCircle size={16} className="mt-0.5 text-emerald-500 shrink-0" />
                        <span>Remote: 50,00 €</span>
                      </li>
                      <li className="flex items-start gap-2 text-muted-foreground">
                        <CheckCircle size={16} className="mt-0.5 text-emerald-500 shrink-0" />
                        <span>Vor-Ort: 70,00 €</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="group relative rounded-2xl border border-navy/[0.12] bg-background p-6 transition-all hover:border-emerald-500/60 hover:shadow-lift">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="font-bold text-navy text-lg mb-2">PC/Laptop-Grundlagen</div>
                    <div className="text-sm text-muted-foreground mb-2">60 Minuten</div>
                    <ul className="space-y-1.5">
                      <li className="flex items-start gap-2 text-muted-foreground">
                        <CheckCircle size={16} className="mt-0.5 text-emerald-500 shrink-0" />
                        <span>Remote: 50,00 €</span>
                      </li>
                      <li className="flex items-start gap-2 text-muted-foreground">
                        <CheckCircle size={16} className="mt-0.5 text-emerald-500 shrink-0" />
                        <span>Vor-Ort: 70,00 €</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="group relative rounded-2xl border border-navy/[0.12] bg-background p-6 transition-all hover:border-emerald-500/60 hover:shadow-lift">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="font-bold text-navy text-lg mb-2">Online-Banking & Sicherheit</div>
                    <div className="text-sm text-muted-foreground mb-2">60 Minuten</div>
                    <ul className="space-y-1.5">
                      <li className="flex items-start gap-2 text-muted-foreground">
                        <CheckCircle size={16} className="mt-0.5 text-emerald-500 shrink-0" />
                        <span>Remote: 50,00 €</span>
                      </li>
                      <li className="flex items-start gap-2 text-muted-foreground">
                        <CheckCircle size={16} className="mt-0.5 text-emerald-500 shrink-0" />
                        <span>Vor-Ort: 70,00 €</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Prepaid-Paket */}
          <div>
            <div className="text-lg font-bold text-navy mb-4 flex items-center gap-2">
              <Shield size={18} className="text-flame" />
              Prepaid-Paket
            </div>
            <div className="space-y-4">
              <div className="group relative rounded-2xl border border-navy/[0.12] bg-background p-6 transition-all hover:border-flame/60 hover:shadow-lift">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="font-bold text-navy text-lg mb-1">Remote-Support Paket S50 (Prepaid)</div>
                    <p className="text-sm text-muted-foreground mb-3 italic">
                      5 × 30 Minuten Remote-Support mit Terminreservierung und Dokumentation
                    </p>
                    <ul className="space-y-1.5">
                      <li className="flex items-start gap-2 text-muted-foreground">
                        <CheckCircle size={16} className="mt-0.5 text-emerald-500 shrink-0" />
                        <span>Insgesamt 2,5 Stunden Remote-Support flexibel einlösbar</span>
                      </li>
                      <li className="flex items-start gap-2 text-muted-foreground">
                        <CheckCircle size={16} className="mt-0.5 text-emerald-500 shrink-0" />
                        <span>Telefonischer Support während der Arbeitszeit</span>
                      </li>
                      <li className="flex items-start gap-2 text-muted-foreground">
                        <CheckCircle size={16} className="mt-0.5 text-emerald-500 shrink-0" />
                        <span>Ersparnis gegenüber Einzelbuchungen</span>
                      </li>
                    </ul>
                  </div>
                  <div className="md:ml-6 flex items-center justify-between gap-3 md:flex-col md:items-end md:justify-start">
                    <div className="text-2xl font-extrabold text-navy leading-tight whitespace-nowrap">140,00 €</div>
                    <Button
                      onClick={() => document.querySelector("#kontakt")?.scrollIntoView({ behavior: "smooth" })}
                      variant="outline"
                      size="sm"
                      className="text-sm rounded-full border-flame text-flame hover:bg-flame hover:text-white"
                    >
                      Jetzt anfragen
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </Section>
  );
};

const FAQ = () => {
  const faqs = [
    {
      question: "Wie funktioniert Remote-Support?",
      answer: "Über eine sichere Verbindung kann ich auf Ihren Computer zugreifen und Probleme direkt lösen. Sie behalten dabei die volle Kontrolle und können die Verbindung jederzeit beenden."
    },
    {
      question: "Welche Zahlungsarten akzeptieren Sie?",
      answer: "Ich akzeptiere Barzahlung, Kartenzahlung, ApplePay, GooglePay, Überweisung und PayPal. Alle Preise sind Endpreise gemäß § 19 UStG (Kleinunternehmerregelung)."
    },
    {
      question: "Gibt es auch Notfall-Support?",
      answer: "Für Business-Kunden biete ich einen Notfall-Support an. Privatkunden können in dringenden Fällen auch außerhalb der regulären Zeiten anrufen."
    },
    {
      question: "Bieten Sie auch Hardware-Reparaturen an?",
      answer: "Ja, kleinere Hardware-Probleme löse ich vor Ort. Bei größeren Defekten arbeite ich mit vertrauenswürdigen lokalen Partnern zusammen."
    }
  ];

  return (
    <Section id="faq" alternate>
      <div className="absolute inset-0 bg-dotgrid opacity-40 pointer-events-none" aria-hidden="true" />

      <div className="relative z-10">
        <SectionHeader
          number="06"
          title="Häufige"
          highlight="Fragen"
          subtitle="Antworten auf die wichtigsten Fragen zu unserem IT-Service"
        />

        <Reveal className="max-w-4xl mx-auto">
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-card rounded-2xl border border-navy/10 px-6 py-2 shadow-soft hover:shadow-lift transition-all duration-300 hover:border-primary/35 data-[state=open]:border-primary/45"
              >
                <AccordionTrigger className="text-left font-bold text-navy hover:text-primary hover:no-underline py-6 text-lg">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pb-6 text-base">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </Section>
  );
};

// Gift Voucher Promotional Banner
const GiftVoucherBanner = () => (
  <Section id="gutschein-banner" className="relative overflow-hidden">
    {/* Warmer Verlauf im Leiterbahn-Orange des Logos */}
    <div className="absolute inset-0 bg-gradient-to-br from-flame/10 via-background to-flame-soft/10 pointer-events-none" aria-hidden="true"></div>
    <div className="absolute inset-0 bg-circuit opacity-60 pointer-events-none" aria-hidden="true"></div>
    <div className="absolute top-10 left-10 w-40 h-40 bg-flame/15 rounded-full blur-3xl" aria-hidden="true"></div>
    <div className="absolute bottom-10 right-10 w-48 h-48 bg-flame-soft/15 rounded-full blur-3xl" aria-hidden="true"></div>

    <Reveal className="relative z-10 max-w-4xl mx-auto text-center">
      <div className="inline-flex items-center gap-2 rounded-full bg-flame/10 border border-flame/25 px-5 py-2 text-flame font-bold mb-6">
        <Gift className="h-4 w-4" />
        Geschenkidee
      </div>

      <h2 className="text-3xl md:text-4xl font-extrabold text-navy mb-6">
        IT-Hilfe verschenken
      </h2>

      <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8 leading-relaxed">
        Das perfekte Geschenk für Eltern oder Großeltern! Professionelle Unterstützung bei Computer,
        Smartphone oder Tablet – <strong className="text-navy">geduldig erklärt, ohne Fachsprache</strong>.
      </p>

      <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
        <div className="flex items-center gap-2 text-foreground/85">
          <CheckCircle className="h-5 w-5 text-emerald-600" />
          <span>Flexibler Betrag (10€ – 500€)</span>
        </div>
        <div className="flex items-center gap-2 text-foreground/85">
          <CheckCircle className="h-5 w-5 text-emerald-600" />
          <span>Sofort per E-Mail</span>
        </div>
        <div className="flex items-center gap-2 text-foreground/85">
          <CheckCircle className="h-5 w-5 text-emerald-600" />
          <span>12 Monate gültig</span>
        </div>
      </div>

      <Link to="/gutschein">
        <Button className="rounded-full bg-gradient-flame px-8 py-6 text-lg font-extrabold text-navy-deep shadow-soft hover:shadow-lift hover:-translate-y-0.5 hover:opacity-95 transition-all">
          <Gift className="mr-2 h-5 w-5" />
          Gutschein kaufen
          <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
      </Link>

      <p className="mt-6 text-sm text-muted-foreground flex items-center justify-center gap-2">
        <Heart className="h-4 w-4 text-flame" />
        Über 100 verschenkte Gutscheine – Freude garantiert!
      </p>
    </Reveal>
  </Section>
);

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "", newsletter_opt_in: false });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast({ title: "Fehler", description: "Bitte füllen Sie alle Pflichtfelder aus.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      await postLead(form);
      toast({ title: "Nachricht gesendet", description: "Vielen Dank für Ihre Anfrage. Wir melden uns bald bei Ihnen!" });
      setForm({ name: "", email: "", phone: "", company: "", message: "", newsletter_opt_in: false });
    } catch (error) {
      toast({ title: "Fehler", description: "Nachricht konnte nicht gesendet werden. Bitte versuchen Sie es erneut.", variant: "destructive" });
    }
    setSubmitting(false);
  };

  return (
    <Section id="kontakt" className="relative overflow-hidden bg-aurora">
      <div className="absolute inset-0 bg-circuit opacity-70 pointer-events-none" aria-hidden="true"></div>

      <div className="relative z-10">
        <Reveal className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold mb-5 text-navy">
            Jetzt <span className="text-primary">Kontakt aufnehmen</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Haben Sie Fragen oder benötigen Sie IT-Unterstützung? Wir helfen Ihnen gerne weiter!
          </p>
        </Reveal>

        <div className="grid gap-10 lg:grid-cols-2 items-start text-left">
          <Reveal className="bg-card rounded-[2rem] p-6 md:p-8 shadow-lift border border-navy/10">
            <h3 className="text-2xl font-bold mb-8 text-navy">Ihre Nachricht an uns</h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label className="block text-base font-bold text-navy mb-3">Name *</label>
                  <Input
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="bg-background border-2 border-input text-foreground placeholder:text-muted-foreground rounded-xl focus:border-primary text-base px-4 py-3 font-medium h-auto"
                    placeholder="Ihr vollständiger Name"
                  />
                </div>
                <div>
                  <label className="block text-base font-bold text-navy mb-3">Telefon *</label>
                  <Input
                    required
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="bg-background border-2 border-input text-foreground placeholder:text-muted-foreground rounded-xl focus:border-primary text-base px-4 py-3 font-medium h-auto"
                    placeholder="Ihre Telefonnummer"
                  />
                </div>
              </div>

              <div>
                <label className="block text-base font-bold text-navy mb-3">E-Mail *</label>
                <Input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="bg-background border-2 border-input text-foreground placeholder:text-muted-foreground rounded-xl focus:border-primary text-base px-4 py-3 font-medium h-auto"
                  placeholder="ihre@email.de"
                />
              </div>

              <div>
                <label className="block text-base font-bold text-navy mb-3">
                  Firmenname (optional)
                  <span className="block text-sm text-muted-foreground font-normal mt-1">
                    🏢 Für Kleinunternehmen & Freiberufler
                  </span>
                </label>
                <Input
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  className="bg-background border-2 border-input text-foreground placeholder:text-muted-foreground rounded-xl focus:border-primary text-base px-4 py-3 font-medium h-auto"
                  placeholder="Name Ihres Unternehmens"
                />
              </div>

              <div>
                <label className="block text-base font-bold text-navy mb-3">Nachricht *</label>
                <Textarea
                  required
                  rows={6}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="bg-background border-2 border-input text-foreground placeholder:text-muted-foreground rounded-xl focus:border-primary resize-none text-base px-4 py-3 font-medium"
                  placeholder="Beschreiben Sie Ihr IT-Problem oder Ihre Anfrage..."
                />
              </div>

              <div className="bg-primary/5 rounded-2xl p-6 border border-primary/20">
                <div className="flex items-start gap-4 mb-3">
                  <input
                    type="checkbox"
                    id="newsletter"
                    checked={form.newsletter_opt_in}
                    onChange={(e) => setForm({ ...form, newsletter_opt_in: e.target.checked })}
                    className="w-6 h-6 rounded accent-[#0E7490] bg-background border-2 border-input mt-1"
                  />
                  <label htmlFor="newsletter" className="text-base text-navy font-bold leading-relaxed">
                    📧 Ja, ich möchte den IT-Newsletter mit hilfreichen Tipps erhalten
                  </label>
                </div>
                <p className="text-sm text-muted-foreground ml-10 leading-relaxed">
                  {form.company && form.company.trim() ? (
                    "🏢 Sie erhalten Business-orientierte IT-Tipps für Kleinunternehmen"
                  ) : (
                    "👴 Sie erhalten verständliche IT-Tipps für Senioren & Privatpersonen"
                  )}
                </p>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-primary hover:bg-primary/90 text-white font-bold py-5 px-8 rounded-full shadow-soft transition-all duration-300 hover:shadow-lift hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed text-lg h-auto"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
                    Wird gesendet...
                  </>
                ) : (
                  <>
                    <Mail size={24} className="mr-3" />
                    Nachricht senden
                  </>
                )}
              </Button>
            </form>
          </Reveal>

          <div className="space-y-8">
            <Reveal delay={100} className="bg-card rounded-[2rem] p-6 md:p-8 shadow-lift border border-navy/10">
              <h3 className="text-2xl font-bold mb-8 text-navy">Kontaktinformationen</h3>

              <div className="space-y-5">
                <a
                  href="tel:+4915128958006"
                  className="flex items-start gap-4 p-6 bg-primary/5 rounded-2xl border border-primary/20 hover:bg-primary/10 hover:border-primary/40 transition-all group"
                >
                  <div className="p-3 bg-primary rounded-xl shadow-soft transition-transform group-hover:scale-110">
                    <Phone size={24} className="text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-navy mb-1 text-lg">Telefon</div>
                    <div className="text-primary font-bold text-xl">01512 895 800 6</div>
                    <div className="text-muted-foreground mt-1 font-medium text-base">Mo-Fr 8:00-18:00 Uhr</div>
                  </div>
                </a>

                <a
                  href="https://wa.me/4915128958006?text=Hallo%2C%20ich%20habe%20eine%20Frage%20zum%20IT-Support."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-4 p-6 bg-green-500/5 rounded-2xl border border-green-500/25 hover:bg-green-500/10 hover:border-green-500/45 transition-all group"
                >
                  <div className="p-3 bg-green-600 rounded-xl shadow-lg transition-transform group-hover:scale-110">
                    <svg viewBox="0 0 24 24" className="h-6 w-6 fill-white">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  </div>
                  <div>
                    <div className="font-bold text-navy mb-1 text-lg">WhatsApp</div>
                    <div className="text-green-700 font-bold text-xl">Direkt schreiben</div>
                    <div className="text-muted-foreground mt-1 font-medium text-base">Schnelle Antwort garantiert</div>
                  </div>
                </a>

                <a
                  href="mailto:kontakt@klartech-mueritz.de"
                  className="flex items-start gap-4 p-6 bg-navy/5 rounded-2xl border border-navy/15 hover:bg-navy/10 hover:border-navy/30 transition-all group"
                >
                  <div className="p-3 bg-navy rounded-xl shadow-lg transition-transform group-hover:scale-110">
                    <Mail size={24} className="text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-navy mb-1 text-lg">E-Mail</div>
                    <div className="text-navy font-bold text-lg break-all">kontakt@klartech-mueritz.de</div>
                    <div className="text-muted-foreground mt-1 font-medium text-base">Antwort innerhalb von 24h</div>
                  </div>
                </a>
              </div>
            </Reveal>

            {/* Google Maps */}
            <Reveal delay={180} className="bg-card rounded-[2rem] overflow-hidden shadow-lift border border-navy/10">
              <div className="p-6 border-b border-border">
                <h3 className="text-xl font-bold text-navy flex items-center gap-3">
                  <MapPin className="h-6 w-6 text-flame" />
                  Standort: Waren (Müritz)
                </h3>
              </div>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d37956.02069!2d12.65!3d53.52!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47ab0a0b4b36e0f5%3A0x4250d7a0f29f1b0!2s17192%20Waren%20(M%C3%BCritz)!5e0!3m2!1sde!2sde!4v1700000000000!5m2!1sde!2sde"
                width="100%"
                height="250"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Standort Klartech Müritz - Waren (Müritz)"
                className="grayscale hover:grayscale-0 transition-all duration-500"
              />
            </Reveal>

            <Reveal delay={240} className="bg-primary/5 rounded-[2rem] border border-primary/20 p-8 shadow-soft">
              <div className="flex items-center gap-4 mb-4">
                <CheckCircle size={32} className="text-primary" />
                <span className="font-bold text-navy text-xl">Kostenloses Erstgespräch</span>
              </div>
              <p className="text-foreground/85 text-lg leading-relaxed font-medium">
                Rufen Sie uns an oder schreiben Sie uns. Wir beraten Sie gerne kostenlos und unverbindlich!
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </Section>
  );
};

const Footer = () => {
  const nav = [
    { label: "Warum", href: "#warum" },
    { label: "Leistungen", href: "#leistungen" },
    { label: "KI-Service", href: "#ki" },
    { label: "Zeiten", href: "#zeiten" },
    { label: "Preise", href: "#preise" },
    { label: "FAQ", href: "#faq" },
    { label: "Blog", href: "/blog", internal: true },
    { label: "Klartech Kompakt", href: "https://kompakt.klartech-mueritz.de", external: true },
    { label: "KI-Chatbot", href: "/chatbot-produkt", internal: true },
  ];

  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <footer className="bg-navy-deep text-white py-16 relative overflow-hidden text-left">
      {/* Leiterbahn-Muster aus dem Logo */}
      <div className="absolute inset-0 bg-circuit-light pointer-events-none" aria-hidden="true"></div>
      <div className="absolute -top-24 right-1/4 w-72 h-72 bg-accent/10 rounded-full blur-3xl pointer-events-none" aria-hidden="true"></div>

      <div className="mx-auto max-w-7xl px-6 relative z-10">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-white rounded-2xl p-3.5 shadow-lift">
                <img
                  src="/images/logo-klartech.png"
                  alt="Klartech Müritz Logo"
                  className="h-9 w-auto object-contain"
                />
              </div>
            </div>
            <p className="text-white/65 leading-relaxed mb-6 max-w-md">
              Ihr vertrauensvoller IT-Partner in Waren (Müritz). Wir bieten geduldige,
              verständliche IT-Unterstützung für Senioren, Privatleute und kleine Unternehmen.
            </p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm text-white/50">
                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                Online verfügbar
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-bold mb-5 text-white flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-flame" aria-hidden="true" />
              Navigation
            </h3>
            <ul className="space-y-3">
              {nav.map((item, index) => (
                <li key={item.href + '-footer-' + index}>
                  {item.external ? (
                    <a
                      href={item.href}
                      className="text-white/60 hover:text-flame-soft transition-colors text-sm"
                    >
                      {item.label}
                    </a>
                  ) : item.internal ? (
                    <Link
                      to={item.href}
                      className="text-white/60 hover:text-flame-soft transition-colors text-sm"
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <button
                      onClick={() => scrollTo(item.href)}
                      className="text-white/60 hover:text-flame-soft transition-colors text-sm"
                    >
                      {item.label}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-bold mb-5 text-white flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-flame" aria-hidden="true" />
              Rechtliches
            </h3>
            <ul className="space-y-3">
              <li>
                <a href="/impressum" className="text-white/60 hover:text-flame-soft transition-colors text-sm">
                  Impressum
                </a>
              </li>
              <li>
                <a href="/datenschutz" className="text-white/60 hover:text-flame-soft transition-colors text-sm">
                  Datenschutz
                </a>
              </li>
              <li>
                <button
                  onClick={() => scrollTo("#kontakt")}
                  className="text-white/60 hover:text-flame-soft transition-colors text-sm"
                >
                  Kontakt
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/45 text-sm">
              © {new Date().getFullYear()} Klartech Müritz. Alle Rechte vorbehalten.
            </p>
            <div className="flex items-center gap-6">
              <a href="tel:+4915128958006" className="text-white/45 hover:text-white transition-colors text-sm">
                +49 (0) 1512 895 800 6
              </a>
              <a href="mailto:kontakt@klartech-mueritz.de" className="text-white/45 hover:text-white transition-colors text-sm">
                kontakt@klartech-mueritz.de
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default function LandingComplete() {
  // SEO für Hauptseite
  useSEO(SEO_PRESETS.home);

  return (
    <SectionsProvider>
      <LandingContent />
    </SectionsProvider>
  );
}

function LandingContent() {
  return (
    <div className="min-h-screen bg-background text-left">
      <SeasonalDecorations />
      <SocialSidebar />
      <ModernNav />
      <main>
        <ModernHero />
        <TrustLogos />

        {/* Christmas Section - Dynamic */}
        <DynamicSection
          slug="christmas_device_setup"
          fallback={<ChristmasDeviceSetup />}
          dynamic={(content) => <DynamicChristmas content={content} />}
        />

        {/* Why Section - Dynamic */}
        <DynamicSection
          slug="why"
          fallback={<Why />}
          dynamic={(content) => <DynamicWhy content={content} />}
        />

        {/* Services Section - Dynamic */}
        <DynamicSection
          slug="services"
          fallback={<Services />}
          dynamic={(content) => <DynamicServices content={content} />}
        />

        {/* KI-Unterstützung – Ankerpunkt (#ki), bewusst nur für kleine Firmen */}
        <AISection />

        {/* Times Section - Dynamic */}
        <DynamicSection
          slug="times"
          fallback={<Times />}
          dynamic={(content) => <DynamicTimes content={content} />}
        />

        {/* Prices Section - Dynamic */}
        <DynamicSection
          slug="prices"
          fallback={<Prices />}
          dynamic={(content) => <DynamicPrices content={content} />}
        />

        {/* FAQ Section - Dynamic */}
        <DynamicSection
          slug="faq"
          fallback={<FAQ />}
          dynamic={(content) => <DynamicFAQ content={content} />}
        />

        {/* Gift Voucher Banner - Dynamic */}
        <DynamicSection
          slug="gift_voucher_banner"
          fallback={<GiftVoucherBanner />}
          dynamic={(content) => <DynamicGiftVoucher content={content} />}
        />

        {/* Chatbot Product Section - Dynamic */}
        <DynamicSection
          slug="chatbot_product"
          fallback={<ChatbotProductSection />}
          dynamic={() => <ChatbotProductSection />}
        />

        {/* Klartext-Check Teaser */}
        <KlartextCheckTeaser />

        <LatestBlogPosts />

        {/* Contact Section - Dynamic */}
        <DynamicSection
          slug="contact"
          fallback={<Contact />}
          dynamic={() => <Contact />}
        />
      </main>
      <Footer />
      <ChatWidget />
    </div>
  );
}
